import React, { useState } from 'react';
import { BrandColor } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';
import { useInTest } from '../../../../hooks';
import { CLIENT_PROPROFILE_GREEN_AND_METAL_CTA_TEST_NAME } from '../../../shared/providerProfile/constants';
import {
  Button,
  BodyText,
  ModalBox,
  DeprecatedMaskedFormField,
} from '../../../ui';
import Checkbox from '../../../ui/Checkbox';
import { isValidPhone } from '../../../../modules/validators';
import ExternalLink from '../../../ui/ExternalLink';

type PhoneNumberProps = {
  consentCheckboxLabel?: string;
  footerText?: string;
  includeTermsLinkInConsentCheckboxLabel?: boolean;
  phoneNumber?: string;
  onClick: (phoneNumber: string) => void;
};

const PhoneNumberAndConsentFormField: React.FCWithChildren<PhoneNumberProps> = ({
  consentCheckboxLabel = 'Yes, send me updates about my pro\'s availability and services',
  footerText = 'By clicking Next you agree to receive appointment updates',
  includeTermsLinkInConsentCheckboxLabel = false,
  phoneNumber = '',
  onClick,
}) => {
  const [consent, setConsent] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>(null);
  const [consentError, setConsentError] = useState<string>('');
  const [phoneNumberValue, setPhoneNumberValue] = useState<string>(phoneNumber);

  const isInGreenButtonTest = useInTest(
    CLIENT_PROPROFILE_GREEN_AND_METAL_CTA_TEST_NAME,
  );

  const validatePhoneNumber = (newPhone: string): string => {
    if (!isValidPhone(newPhone)) {
      return 'Invalid phone number';
    }

    return null;
  };

  const handlePhoneChange = event => {
    const newPhone = event.target.value;

    setPhoneNumberValue(newPhone);
    setPhoneError(validatePhoneNumber(newPhone));
  };

  const handleConsentChange = () => {
    setConsent(!consent);
    setConsentError('');
  };

  const handleOnClick = () => {
    if (!phoneNumberValue) {
      setConsentError('A valid phone number is required to continue.');
      return;
    }

    if (validatePhoneNumber(phoneNumberValue)) {
      setConsentError('A valid phone number is required to continue.');
      return;
    }

    if (!consent) {
      setConsentError('Your consent is required to continue.');
      return;
    }

    onClick(phoneNumberValue);
  };
  const renderFooter = () => (
    <Box>
      <Inline paddingBottom={16} justifyContent="center">
        <BodyText appearance="small" color={BrandColor.Red} testID="phone-number-form-consent-error">
          {consentError}
        </BodyText>
      </Inline>
      <Button
        appearance="primary-neue"
        onClick={handleOnClick}
        disabled={!phoneNumberValue || phoneNumberValue === '' || phoneError !== null}
        text="Next"
        testID="phone-number-form-next-button"
        style={
          isInGreenButtonTest ? {
            backgroundColor: BrandColor.Green,
          } : {}
        }
      />
      <Inline paddingBottom={16} paddingTop={16} justifyContent="center">
        <BodyText appearance="small">
          {footerText}
        </BodyText>
      </Inline>
    </Box>
  );

  return (
    // @ts-ignore
    <ModalBox padding={0} scrollable renderFooter={renderFooter}>
      <Box testID="phone-number-form">
        <Box>
          <DeprecatedMaskedFormField
            mask="(999) 999-9999"
            accentColor={BrandColor.Red}
            message={phoneError}
            name="phonenumber"
            id="phonenumber"
            autoComplete="tel"
            borderColor={BrandColor.Silver}
            style={{
              borderRadius: 4,
            }}
            label={(
              <BodyText appearance="small">
                Phone number
                <BodyText color={BrandColor.Ash} appearance="small">{' (required)'}</BodyText>
              </BodyText>
            )}
            aria-label="phone number"
            type="phone"
            value={phoneNumberValue}
            onValueChange={handlePhoneChange}
            testID="phone-number-form-input"
          />
        </Box>
        <Inline
          space={8}
          paddingY={16}
        >
          <Checkbox
            testID="phone-number-form-consent-button"
            onChange={handleConsentChange}
            uncheckedAppearance="normal"
          />
          <BodyText color={BrandColor.Metal} appearance="small">
            {consentCheckboxLabel}
            {includeTermsLinkInConsentCheckboxLabel && (
              <BodyText color={BrandColor.Metal} appearance="small">
                {' View '}
                <ExternalLink
                  href="https://www.styleseat.com/privacy"
                  target="_system"
                  color={BrandColor.DarkBlue}
                >
                  Privacy
                </ExternalLink>
                {' & '}
                <ExternalLink
                  href="https://www.styleseat.com/tos-for-clients"
                  target="_system"
                  color={BrandColor.DarkBlue}
                >
                  Terms
                </ExternalLink>
              </BodyText>
            )}
          </BodyText>
        </Inline>
      </Box>
    </ModalBox>
  );
};

export default PhoneNumberAndConsentFormField;
